import React from 'react';

import styled from 'styled-components';

import InnerHtml from 'components/InnerHtml/InnerHtml';
import SmallPrint from 'components/SmallPrint';

import { RowBorderTableI } from 'interfaces/content-stack/modular-blocks/basicTableBlock';

import { buildBasicRows } from 'utils/functions';

const TableTitle = styled(SmallPrint)`
  font-style: normal;
`;

const Table = styled.table<{ $lineHeight: number; $contentAlignment?: string }>`
  ${(props) => `
    font-family: ${props.theme.fonts.default};
    line-height: ${props.$lineHeight};
    text-align: ${props.$contentAlignment};
  `}
  margin-bottom: 1.5rem;
  width: 100%;

  &.content-padding-extra {
    td,
    th {
      padding: 1rem 1.5rem;

      @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
        padding: 0.75rem;
      }
    }

    td {
      @media (max-width: ${(props) => props.theme.breakpoints.md}) {
        padding-left: 1rem;
        padding-right: 1rem;
      }

      @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
        padding: 0.75rem;
      }
    }

    th {
      @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }

      @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }

      @media (max-width: ${(props) => props.theme.breakpoints.sm}) {
        padding-left: 0.75rem;
        padding-right: 0.75rem;
      }

      @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
        padding-top: 0.25rem;
        padding-bottom: 0.25rem;
      }
    }
  }

  &.text-weight-semibold {
    td,
    th {
      font-weight: 600;
    }
  }
`;

const TableRow = styled.tr`
  &:nth-child(odd) td {
    background-color: ${(props) => props.theme.colors.secondaryGray5};
  }
`;

const TableHeader = styled.th<{ $width?: number }>`
  text-transform: uppercase;
  width: ${(props) => (props.$width ? `${props.$width}%` : 'auto')};
  font-weight: 600;
  font-size: 0.75rem;
  padding: 0.75rem 0.5rem;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding: 0.5rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 0.66rem;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    text-align: center;
  }
`;

const TableData = styled.td`
  vertical-align: top;
  font-size: 1rem;
  font-weight: 500;
  padding: 0.75rem;

  &:not(:last-child) {
    border-right: 0.0625rem solid ${(props) => props.theme.colors.borderLightGray2};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: 0.8125rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
`;

function RowBorderTable({
  tableColumns,
  tableHeaders,
  lineHeight,
  tableTitle,
  columnContentAlignment,
  cssClassname,
}: RowBorderTableI) {
  const rows = buildBasicRows(tableColumns);

  return (
    <>
      {tableTitle && (
        <TableTitle dark bold>
          {tableTitle}
        </TableTitle>
      )}
      <Table className={cssClassname} $contentAlignment={columnContentAlignment} $lineHeight={lineHeight}>
        {tableHeaders.length > 0 && (
          <thead>
            <TableRow>
              {tableHeaders.map((header, index) => (
                <TableHeader key={index} $width={header.headerWidth}>
                  {header.content}
                </TableHeader>
              ))}
            </TableRow>
          </thead>
        )}
        {rows.length > 0 && (
          <tbody>
            {rows.map((row: any, index: number) => (
              <TableRow key={index}>
                {row.map((item: any, index: number) => {
                  const cleanContent = item?.content.replace(/<\/?p[^>]*>/g, '');

                  return (
                    <React.Fragment key={index}>
                      {cleanContent && (
                        <TableData>
                          <InnerHtml richText={cleanContent} />
                        </TableData>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </tbody>
        )}
      </Table>
    </>
  );
}

export default React.memo(RowBorderTable);
